// Quarters only, so any row a coach builds tiles exactly (ADR-026).
export const PANEL_SPANS = ['full', 'three-quarters', 'half', 'quarter'] as const

export type PanelSpan = (typeof PANEL_SPANS)[number]

export const isPanelSpan = (value: unknown): value is PanelSpan => PANEL_SPANS.includes(value as PanelSpan)

export const SPAN_LABELS: Record<PanelSpan, string> = {
  full: 'Full',
  'three-quarters': '¾',
  half: '½',
  quarter: '¼',
}

export const GRID_COLUMNS = { xs: 4, md: 8, lg: 12 } as const

// `xs` is deliberately flattened: a quarter of four columns is a sliver, so only `quarter` differs there.
const SPAN_COLUMNS: Record<PanelSpan, { xs: number; md: number; lg: number }> = {
  full: { xs: 4, md: 8, lg: 12 },
  'three-quarters': { xs: 4, md: 6, lg: 9 },
  half: { xs: 4, md: 4, lg: 6 },
  quarter: { xs: 2, md: 2, lg: 3 },
}

export const gridColumnFor = (span: PanelSpan) => {
  const columns = SPAN_COLUMNS[span]

  return { xs: `span ${columns.xs}`, md: `span ${columns.md}`, lg: `span ${columns.lg}` }
}

type Breakpoint = keyof typeof GRID_COLUMNS

// Takes the grid's resolved column count, never the window — the two drift. `null` for a hidden
// grid, which reports its specified tracks rather than real ones.
const breakpointForColumns = (columns: number): Breakpoint | null =>
  (Object.keys(GRID_COLUMNS) as Breakpoint[]).find((key) => GRID_COLUMNS[key] === columns) ?? null

export const spanForColumns = (columns: number, gridColumns: number): PanelSpan | null => {
  const breakpoint = breakpointForColumns(gridColumns)

  if (!breakpoint) {
    return null
  }

  const distance = (span: PanelSpan) => Math.abs(SPAN_COLUMNS[span][breakpoint] - columns)

  return PANEL_SPANS.reduce((closest, span) => (distance(span) < distance(closest) ? span : closest))
}
